// src/components/DemandResponseTable.tsx
import * as React from 'react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Tooltip from '@mui/material/Tooltip';
import { openResources } from '../authConfig';
import { TableSortLabel } from '@mui/material';

interface Data {
    loadGroup: string;
    do: string[];
    [key: string]: any;
}

interface DemandResponseLogProps {
    coop: string;
    defaultStartDate: Date;
    defaultEndDate: Date
}

const getColor = (value: string) => {
    if (value === 'ON') return 'rgb(0, 95, 0)';
    if (value === 'OFF') return 'rgb(139, 0, 0)';
    return 'inherit';
}

const getTextColor = (value: string) => {
    if (value === 'ON') return 'white';
    if (value === 'OFF') return 'white';
    return 'black';
}

const toolTipDataLoadGroup: Record<string, string> = {
    '1': 'Short-Term Loads (water heaters)',
    '2': 'Intermediate-Term Loads (storage heat)',
    '3': 'Long-Term Loads (dual heating furnaces, back-up generators)',
    '6': 'Summer-Only Loads (irrigation, cycled air conditioning)',
    '9.99': 'Internal Network Transmission',
}

const toolTipData: Record<string, Record<string, string>> = {
    '1.01': {
        '9': 'Grain Dryers',
        '10': 'Pk Ind Light',
        '11': 'Water Heaters',
        '12': 'Water Heaters',
        '13': 'Water Heaters',
        '14': 'Water Heaters',
        '15': 'Clothes Dryer',
        '16': 'Office Heat',
        '17': 'Space Heat',
        '18': 'Space Heat',
        '19': 'Warehouse Fans',
        '20': 'Warehouse Fans',
        '21': 'Warehouse Heat',
        '22': 'Warehouse Heat',
        '23': 'Warehouse Heat',
        '24': 'Warehouse Heat',
    },
    '1.02': {
        '11': 'Water Heaters',
        '12': 'Water Heaters',
        '13': 'Water Heaters',
        '14': 'Water Heaters',
        '15': 'Misc Heat 1',
        '16': 'Misc Heat 1',
        '17': 'Water Heaters',
        '18': 'Water Heaters',
        '19': 'Water Heaters',
        '20': 'Water Heaters',
        '24': 'Ind. Controlled Loads 1',
    },
    '2.01': {
        '9': 'Slab Heat',
        '10': 'Slab Heat',
        '11': 'Slab Heat',
        '12': 'Slab Heat',
        '13': 'Slab Heat',
        '14': 'Slab Heat',
        '15': 'Misc Heat 2',
        '16': 'Misc Heat 2',
        '17': 'Thermal Storage Heat',
        '18': 'Thermal Storage Heat',
        '19': 'Water Heaters',
        '20': 'Water Heaters',
        '21': 'Thermal Storage Heat',
        '22': 'Thermal Storage Heat',
        '23': 'Thermal Storage Heat',
        '24': 'Ind. Controlled Loads 2',
    },
    '2.02': {
        '12': 'Misc Loads 2',
        '13': 'Misc Loads 2',
        '19': 'Water Heaters',
        '20': 'Water Heaters',
        '24': 'Ind. Controlled Loads 2',
    },
    '2.03': {
        '9': 'Slab Heat',
        '10': 'Slab Heat',
        '11': 'Slab Heat',
        '12': 'Slab Heat',
        '13': 'Slab Heat',
        '14': 'Slab Heat',
        '15': 'Misc Heat 2',
        '16': 'Misc Heat 2',
        '17': 'Thermal Storage Heat',
        '18': 'Thermal Storage Heat',
        '19': 'Water Heaters',
        '20': 'Water Heaters',
        '21': 'Thermal Storage Heat',
        '22': 'Thermal Storage Heat',
        '23': 'Thermal Storage Heat',
        '24': 'Ind. Controlled Loads 2',
    },
    '2.04': {
        '12': 'Misc Loads 2',
        '13': 'Misc Loads 2',
        '19': 'Water Heaters',
        '20': 'Water Heaters',
        '24': 'Ind. Controlled Loads 2',
    },
    '2.06': {
        '9': 'Battery Storage',
        '16': 'Battery Storage',
    },
    '3.01': {
        '9': 'Dual Heat',
        '10': 'Dual Heat',
        '11': 'Dual Heat',
        '12': 'Dual Heat',
        '13': 'Dual Heat',
        '14': 'Dual Heat',
        '15': 'Dual Heat',
        '16': 'Dual Heat',
        '17': 'Misc Heat 3',
        '18': 'Misc Heat 3',
        '19': 'Dual Heat',
        '20': 'Dual Heat',
        '21': 'Dual Heat',
        '22': 'Dual Heat',
        '24': 'Ind. Controlled Loads 3',
    },
    '3.06': {
        '9': 'Interruptible Alert',
        '10': 'Industrial Loads',
        '11': 'Commercial',
        '12': 'Commercial',
        '13': 'Commercial',
        '14': 'Commercial',
        '15': 'Industrial',
        '16': 'Commercial',
        '17': 'Commercial',
        '18': 'Commercial',
        '19': 'Commercial',
        '23': 'Ind. Controlled Loads 3',
        '24': 'Ind. Controlled Loads 3',
    },
    '3.07': {
        '9': 'Interruptible Alert',
        '10': 'Comm, Direct Control',
        '11': 'Comm, Direct Control',
        '12': 'Misc Loads 3',
        '13': 'Misc Loads 3',
        '14': 'Comm, Direct Control',
        '15': 'Comm, Direct Control',
        '16': 'Comm, Direct Control',
        '17': 'Misc Loads 3',
        '18': 'Misc Loads 3',
        '19': 'Comm, Direct Control',
        '20': 'Comm, Direct Control',
        '21': 'Ind Controlled Loads 3',
        '22': 'Voltage Reduction',
        '23': 'Comm, Indirect Control',
        '24': 'Ind Controlled Loads 3',
    },
    '3.09': {
        '9': 'Dual Heat',
        '10': 'Dual Heat',
        '11': 'Dual Heat',
        '12': 'Dual Heat',
        '13': 'Dual Heat',
        '14': 'Dual Heat',
        '15': 'Dual Heat',
        '16': 'Dual Heat',
        '17': 'Misc Heat 3',
        '18': 'Misc Heat 3',
        '24': 'Ind. Contr Loads 3',
    },
};

const columnHeaders = [
    "Date Time",
    "Area",
    "Load Group",
    "DO9", "DO10", "DO11", "DO12", "DO13", "DO14", "DO15",
    "DO16", "DO17", "DO18", "DO19", "DO20", "DO21", "DO22",
    "DO23", "DO24"
];

const getToolTipTitle = (loadGroup: string, doubleOrder: string): string => {
    return toolTipData[loadGroup]?.[doubleOrder] ?? 'Unknown';
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.primary.dark,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        width: 50,
        //style: {text-align: "center"}
        //text-align: "center"
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (
        event: React.MouseEvent<HTMLButtonElement>,
        newPage: number,
    ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
}

export default function DemandResponseLogMobile2({ coop, defaultStartDate, defaultEndDate }: DemandResponseLogProps) {

    const [data, setData] = useState<Data[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const [startDate, setStartDate] = React.useState<Date | null>(defaultStartDate);
    const [endDate, setEndDate] = React.useState<Date | null>(defaultEndDate);
    const [hideDatePickers, setHideDatePickers] = useState(true);
    const [orderBy, setOrderBy] = useState<string>('dateTime');
    const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('desc');

    const sortedData = React.useMemo(() => {
        if (orderBy === 'dateTime') {
            return [...data].sort((a, b) => {
                const dateA = new Date(a.dateTime).getTime();
                const dateB = new Date(b.dateTime).getTime();
                return orderDirection === 'asc' ? dateA - dateB : dateB - dateA;
            });
        }
        return data;
    }, [data, orderBy, orderDirection]);

    useEffect(() => {
        // Function to fetch data from the API
        const fetchData = async () => {
            try {

                const parseDate = (dateString: string) => {
                    //return new Date();
                    const year = `20${dateString.slice(0, 2)}`;
                    const month = dateString.slice(2, 4);
                    const day = dateString.slice(4, 6);
                    const hours = dateString.slice(7, 9);
                    const minutes = dateString.slice(9, 11);
                    return new Date(`${year}-${month}-${day}T${hours}:${minutes}:00`);
                };

                const formatDate = (date: Date) => {
                    return date.toLocaleString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    });
                };

                //`${baseUrl}${endpoint}`
                const response = await axios.get(openResources.ripple.listEndpoint, {
                    params: {
                        area: coop, //sessionStorage.getItem('coop'), //|| 'Red River'
                        startDate: startDate ? startDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0], //.split('T')[0]
                        endDate: endDate ? endDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0] //.split('T')[0]
                    }
                });
                //console.log("Response data:", endDate); // Debugging line
                if (response.data && Array.isArray(response.data)) {
                    const adaptedData: Data[] = response.data.map((item: any) => ({
                        loadGroup: item.lg,
                        do: item.do,
                        area: item.ac,
                        coop: item.coop,
                        dateTime: formatDate(parseDate(item.datetime)),
                        time: item.datetime,
                        err: item.err,
                        name: item.name,
                    }));
                    setData(adaptedData);
                } else {
                    setData([]);
                }
                setPage(0);
            } catch (error: any) {
                setError('Error fetching data: ' + error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate]);

    useEffect(() => {
        if (data.length === 0 && page > 0) {
            setStartDate((prevDate) => {
                const newDate = new Date(prevDate!);
                newDate.setDate(newDate.getDate() - 1);
                return newDate;
            });
            setEndDate((prevDate) => {
                const newDate = new Date(prevDate!);
                newDate.setDate(newDate.getDate() - 1);
                return newDate;
            });
        }
    }, [data.length, page]);

    useEffect(() => {
        setData([]);
        setPage(0);
        setLoading(true);
    }, [defaultStartDate, defaultEndDate]);

    useEffect(() => {
        setPage(0);
    }, [coop, startDate, endDate]);
    // Avoid a layout jump when reaching the last page with empty rows.
    //const emptyRows =
    //  page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0

    if (loading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;

    return (
        // 1. Change to use 'minHeight: 100vh' to prevent vertical scrollbar doubling, 
        // and add `position: 'relative'` for mobile scrolling context.
        <div style={{ maxWidth: 2100, margin: 'auto', display: 'flex', flexDirection: 'column', height: '100vh', position: 'relative' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                {/* Header (Buttons and Date Pickers) - Fixed height content */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, flexShrink: 0 }}>
                    <ButtonGroup variant="contained" aria-label="outlined primary button group">
                        <Button onClick={() => {
                            const today = new Date();
                            //const tomorrow = new Date(today.getTime());
                            //tomorrow.setDate(today.getDate() + 1);
                            setStartDate(today);
                            //setEndDate(tomorrow);
                            setEndDate(today);
                            setHideDatePickers(true); // Hide date pickers
                        }}
                            style={{
                                backgroundColor: startDate?.toDateString() === new Date().toDateString() ? '#007BFF' : '#1E5AB9',
                                //color: startDate?.toDateString() === new Date().toDateString() ? '#FFFFFF' : '#000000'
                            }}>Today</Button>

                        <Button onClick={() => {
                            //const today = new Date();
                            const yesterday = new Date(); //new Date(today.getTime());
                            yesterday.setDate(yesterday.getDate() - 1);
                            setStartDate(yesterday);
                            setEndDate(yesterday);
                            //setEndDate(today);
                            setHideDatePickers(true); // Hide date pickers
                        }}
                            style={{
                                backgroundColor: startDate?.toDateString() === new Date(new Date().setDate(new Date().getDate() - 1)).toDateString() ? '#007BFF' : '#1E5AB9',
                                //color: startDate?.toDateString() === new Date(new Date().setDate(new Date().getDate() - 1)).toDateString() ? '#FFFFFF' : '#000000'
                            }}>Yesterday</Button>

                        <Button onClick={() => {
                            const archiveStart = new Date();
                            archiveStart.setDate(archiveStart.getDate() - 31); // Example: 30 days ago
                            const archiveEnd = new Date();
                            setStartDate(archiveStart);
                            setEndDate(archiveEnd);
                            setHideDatePickers(false); // Show date pickers
                        }}
                            style={{
                                backgroundColor: startDate && endDate && startDate < new Date(new Date().setDate(new Date().getDate() - 31)) ? '#007BFF' : '#1E5AB9',
                                //color: startDate && endDate && startDate < new Date(new Date().setDate(new Date().getDate() - 29)) ? '#FFFFFF' : '#000000'
                            }}>Archive</Button>
                    </ButtonGroup>
                </Box>
                <div style={{ flexShrink: 0 }}> {/* This div is also fixed height */}
                    <Box
                        sx={{
                            display: 'flex',
                            paddingTop: '10px',
                            justifyContent: 'space-around',
                            mb: 2,
                            visibility: hideDatePickers ? 'hidden' : 'visible',
                            height: hideDatePickers ? 0 : 'auto',
                            overflow: 'hidden',
                        }}
                    >
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                        />
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                        />
                    </Box>
                </div>

                {/* Table Container - Takes up all remaining space and is scrollable */}
                <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <TableContainer component={Paper} sx={{ maxHeight: 'none' }}> {//maxHeight: '100%', overflowY: 'auto' }}>
                    }
                        <Table sx={{ minWidth: 1000 }} aria-label="custom pagination table">
                            <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}>
                                <StyledTableRow>
                                    {columnHeaders.map((header, index) => {
                                        const isSortable = header === 'Date Time';
                                        const sortKey = 'dateTime';
                                        return (
                                            <StyledTableCell key={index} style={{ textAlign: 'center' }}>
                                                {isSortable ? (
                                                    <TableSortLabel
                                                        active={orderBy === sortKey}
                                                        direction={orderDirection}
                                                        onClick={() => {
                                                            const isAsc = orderBy === sortKey && orderDirection === 'asc';
                                                            setOrderDirection(isAsc ? 'desc' : 'asc');
                                                            setOrderBy(sortKey);
                                                        }}
                                                        sx={{
                                                            color: 'white',
                                                            '&.Mui-active': {
                                                                color: 'white',
                                                            },
                                                            '& .MuiTableSortLabel-icon': {
                                                                color: 'white !important',
                                                                opacity: 1,
                                                            },
                                                        }}
                                                    >
                                                        {header}
                                                    </TableSortLabel>
                                                ) : (
                                                    header
                                                )}
                                            </StyledTableCell>
                                        );
                                    })}
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {(rowsPerPage > 0
                                    ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    : data
                                ).map((row) => (
                                    <StyledTableRow key={`${row.loadGroup}-${row.time}`}>
                                        <StyledTableCell component="th" scope="row" style={{ textAlign: 'center', paddingTop: '0px', paddingBottom: '0px' }}>
                                            {row.dateTime}
                                        </StyledTableCell>
                                        <StyledTableCell component="th" scope="row" style={{ textAlign: 'center', paddingTop: '0px', paddingBottom: '0px' }}>
                                            {row.area}
                                        </StyledTableCell>
                                        <StyledTableCell component="th" scope="row" style={{ textAlign: 'center', paddingTop: '0px', paddingBottom: '0px' }}>
                                            <Tooltip title={toolTipDataLoadGroup[row.loadGroup.replace(/\.\d+$/, "")] || 'Unknown'}>
                                                <span>{row.loadGroup}</span>
                                            </Tooltip>
                                        </StyledTableCell>
                                        {row.do.map((value, index) => (
                                            <StyledTableCell key={index} style={{ backgroundColor: getColor(value), color: getTextColor(value), textAlign: 'center', paddingTop: '0px', paddingBottom: '0px' }}>
                                                <Tooltip title={getToolTipTitle(row.loadGroup, (index + 9).toString())}>
                                                    <span>{value}</span>
                                                </Tooltip>
                                            </StyledTableCell>
                                        ))}
                                    </StyledTableRow>
                                ))}
                                {emptyRows > 0 && (
                                    <TableRow style={{ height: 53 * emptyRows }}>
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                            <TableFooter sx={{ 
                                position: 'sticky', // Make the footer sticky
                                bottom: 0,         // Stick it to the bottom of its parent scroll container
                                zIndex: 2,         // Keep it visible above the table body content
                                backgroundColor: 'white' // Ensure the pagination background is solid
                            }}>
                                <TableRow>
                                    <TableCell colSpan={columnHeaders.length} sx={{ p: 0, borderBottom: 'none' }}>
                                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                            <TablePagination
                                            rowsPerPageOptions={[20, 40, 60, { label: 'All', value: -1 }]}
                                            count={data.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            slotProps={{
                                                select: {
                                                inputProps: {
                                                    'aria-label': 'rows per page',
                                                },
                                                native: true,
                                                },
                                            }}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            ActionsComponent={TablePaginationActions}
                                            sx={{ flex: 1 }}
                                            />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </Box>

                {/* Footer (Pagination and Status Boxes) - Fixed height content, positioned outside the scrollable area */}
                
                    {/*<TablePagination
                        // Remove sx={{ overflow: 'visible' }} since we added overflowX: 'auto' to the Box
                        rowsPerPageOptions={[20, 40, 60, { label: 'All', value: -1 }]}
                        colSpan={16}
                        count={data.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        slotProps={{
                            select: {
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            },
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                    />*/}
                
                {/* 3. Add flexShrink: 0 to status box to prevent it from collapsing or shrinking */}
                <Box sx={{ mt: 2, px: 2, py: 1, width: '100%', flexShrink: 0, maxHeight: { xs: '30vh', sm: 'auto' }, overflowY: { xs: 'auto', sm: 'visible' } }}> 
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4, flexWrap: 'wrap'}}>
                        <div className="load-group-container">
                            <div className="load-group-categories" >
                                <h3>{"Load Group Categories"}</h3>
                                <p>{"1 : Short-Term Loads(water heaters)"}</p>
                                <p>{"2 : Intermediate-Term Loads (storage heat)"}</p>
                                <p>{"3 : Long-Term Loads (dual heating furnaces, back-up generators)"}</p>
                                <p>{"6 : Summer-Only Loads (irrigation, cycled air conditioning)"}</p>
                                <p>{"9.99 : Internal Network Transmission"}</p>
                            </div>
                            <div className="load-group-categories" >
                                <h3>{"Status"}</h3>
                                <p>{"ON : Device is not being controlled."}</p>
                                <p>{"OFF : Device is being controlled."}</p>
                                <p>{"--- : N/A or status defaults to previous state."}</p>
                            </div>
                        </div>
                    </Box>
                </Box>
            </LocalizationProvider>
        </div>
    );
}